function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isImpersonatedAccountResponse(json: unknown): boolean {
  if (!isRecord(json)) return false
  const { data } = json
  if (!isRecord(data)) return false
  const { account } = data
  if (!isRecord(account)) return false
  return account.isImpersonation === true
}

let suspended = false
const listeners = new Set<() => void>()

function notifyListeners(): void {
  for (const listener of listeners) listener()
}

// For UI (e.g. the "Mocked" ribbon) that needs to know the moment suspension happens, not just on
// its next incidental re-render — the scenario itself only ever changes via a full reload, but
// suspension can happen mid-session.
export function subscribeToImpersonationGuard(
  listener: () => void,
): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

// One-way latch: an admin's own mocktail scenario must never leak into a customer's account when
// they impersonate it for support. `GetAccount`'s `isImpersonation` field is the same
// server-derived signal the rest of the app already treats as canonical (help widget, changelog
// banners, AdminDeleteSentPostMenuItem) — and since every intercepted response is already fetched
// from the real backend before any patching happens, checking it here costs nothing extra.
// Starting or stopping impersonation is always a full page reload, so this never needs to
// un-latch: a fresh reload gets a fresh, unsuspended guard that re-derives the truth from scratch.
export async function checkForImpersonation(json: unknown): Promise<void> {
  if (suspended || !isImpersonatedAccountResponse(json)) return

  suspended = true
  notifyListeners()
  // eslint-disable-next-line no-console -- mirrors mocktailHandlers' own patched-response banner
  console.log(
    '⚠️ %c[mocktail] impersonation detected — mocking suspended for this session',
    'background: #ffcd85; color: #000; font-weight: bold;',
  )

  const { stopMocktailWorker } = await import('./mocktailWorker')
  stopMocktailWorker()
}

export function isMockingSuspended(): boolean {
  return suspended
}

export function resetImpersonationGuardForTests(): void {
  suspended = false
}
